<template>
	<div class="table-layout">
		<div class="title d-flex">
			<h5 class="table-title" v-if="title">
				{{ title }}
				<date-picker
					v-if="isRange"
					v-model="range"
					type="date"
					range
					format="YYYY-MM-DD"
					value-type="date"
					:lang="vue2DatepickerLang"
					@input="$emit('range', range)"
				/>
			</h5>
			<div class="detail-btns">
				<slot></slot>
			</div>
		</div>
		<div class="sub-com">
			<slot name="sub-com" />
		</div>
		<b-table
			sticky-header
			show-empty
			:fields="headers"
			:items="list"
			:busy="isLoading"
			@row-clicked="onRow"
			:selectable="isSelected"
			:tbody-tr-class="trClass"
			ref="table"
			@row-selected="onRowSelected"
		>
			<!--sorting-->
			<template #head()="data">
				<div
					:class="{ 'sortable-header': data.field.isSort }"
					@click="
						data.field.isSort &&
						$emit(
							'sort',
							data.field.key,
							data.field.sort === 'ASC' ? 'DESC' : 'ASC'
						)
					"
				>
					<span>{{ data.label }}</span>
					<img
						v-if="data.field.isSort"
						class="sort-img"
						:class="data.field.sort ? 'active' : 'unactive'"
						:src="
							require(`@/assets/img/sort-arrow-${
								data.field.sort === 'ASC' ? 'up' : 'down'
							}.svg`)
						"
					/>
				</div>
			</template>
			<template #head(selected)>
				<span class="sr-only">{{ allChecked }}</span>
				<img
					:class="selectedClass"
					:src="
						require(`@/assets/img/form-checkbox-check-${
							allChecked ? 'on' : 'off'
						}.png`)
					"
					@click="allChecked = !allChecked"
				/>
			</template>
			<!--로딩-->
			<template #table-busy>
				<div class="text-center text-secondary my-2">
					<b-spinner class="align-middle"></b-spinner>
					<strong>Loading...</strong>
				</div>
			</template>
			<!--데이터가 없을 때-->
			<template #empty>
				<div v-html="noContent" />
			</template>
			<template #cell(selected)="{ rowSelected }">
				<span class="sr-only">{{ rowSelected }}</span>
				<img
					:class="rowSelected ? 'on' : 'off'"
					:src="
						require(`@/assets/img/form-checkbox-check-${
							rowSelected ? 'on' : 'off'
						}.png`)
					"
				/>
			</template>
			<template #cell()="row">
				<button
					v-if="row.field.link"
					class="detail-btn"
					v-text="row.value"
					type="button"
					@click.stop="onRow(row.item, row.index, row.value)"
				/>
				<div v-else-if="row.value.img" :class="row.value.value">
					<img :src="row.value.img" />
					<p>{{ row.value.text }}</p>
				</div>
				<p
					v-else-if="
						row.field.key === 'memo' || row.field.key === 'content'
					"
					v-b-tooltip.hover.html="toHTML(row.value)"
					v-html="toHTML(row.value)"
				></p>
				<!--
					보조 텍스트가 있는 셀 (헤더에 subKey 를 선언한 경우).
					이름은 짧게 두고 소속 그룹·구분만 아래에 회색으로 덧붙인다.
					mainKey 를 함께 주면 본문에 그 값을 쓴다(전체 라벨 대신 순수 이름).
				-->
				<div v-else-if="row.field.subKey" class="cell-with-sub">
					<p>{{ row.item[row.field.mainKey] || row.value }}</p>
					<span v-if="row.item[row.field.subKey]" class="cell-sub">
						{{ row.item[row.field.subKey] }}
					</span>
				</div>
				<p v-else>
					{{ row.value }}
				</p>
			</template>
			<!--상태 / badge 형태-->
			<template #cell(status)="data">
				<badges
					v-if="data.field.type === 'select-status'"
					:text="data.item.status.text"
					:color="data.item.status.color"
					:textColor="data.item.status.textColor"
					:options="status.issue"
					isBadge
					@select="(option) => $emit('status', option, data.item.id)"
					dropright
				/>
				<b-badge
					v-else
					:style="getBadgeStyle(data.value.color)"
				>{{ data.value.text }}</b-badge>
			</template>
			<!--정산상태 / badge 형태-->
			<template #cell(isSettle)="data">
				<badges
					v-if="data.field.type === 'select-status'"
					:text="data.item.status.text"
					:color="data.item.status.color"
					:textColor="data.item.status.textColor"
					:options="status.issue"
					isBadge
					@select="(option) => $emit('status', option, data.item.id)"
					dropright
				/>
				<b-badge
					v-else
					:style="getBadgeStyle(data.value.color)"
				>{{ data.value.text }}</b-badge>
			</template>

			<!--페이지 이동 / 고객사 이동-->
			<template #cell(client)="row">
				<button
					class="detail-btn"
					type="button"
					v-text="row.value"
					@click.stop="onRow(row.item, row.index, row.value)"
				/>
			</template>
			<template #cell(disposalName)="row">
				<button
					class="detail-btn"
					type="button"
					v-text="row.value"
					@click.stop="row.item.disposalClientId && onRow(row.item, row.index, row.value)"
				/>
			</template>
			<!--다운로드 버튼-->
			<template #cell(download)="row">
				<a
					v-if="!row.item.downloadDisabled"
					:href="`${fileUrl}${row.item.url}`"
					download
					target="_blank"
					class="v-btn v-btn--has-bg theme--light v-size--default"
				>
					<span class="download-text">
						<img
							:src="require('@/assets/img/button-download.png')"
						/>
						다운로드
					</span>
				</a>
				<button
					v-else
					type="button"
					disabled
					class="v-btn v-btn--has-bg theme--light v-size--default v-btn--disabled"
					:title="row.item.downloadDisabledText || '권한이 없는 시험항목입니다.'"
				>
					<span class="download-text">
						<img
							:src="require('@/assets/img/button-download.png')"
						/>
						다운로드
					</span>
				</button>
			</template>
			<!--배열 다운로드-->
			<template #cell(attachment)="row">
				<a
					v-for="(sub, idx) in row.item.attachment"
					:key="`attachment${idx}`"
					:href="`${fileUrl}${sub.url}`"
					download
					target="_blank"
					class="v-btn v-btn--has-bg theme--light v-size--default attachment-link"
				>
					<span class="download-text">
						<img
							:src="require('@/assets/img/button-download.png')"
						/>
						파일 다운로드
					</span>
				</a>
			</template>
			<!--수정버튼-->
			<template #cell(edit)="row">
				<div @click.stop>
					<e-btn
						text="수정"
						:icon="require('@/assets/img/button-edit.png')"
						:disabled="row.field.disabled || row.item.editDisabled"
						@click="$emit('edit', row.item)"
					/>
				</div>
			</template>
			<!--복제 버튼-->
			<template #cell(copy)="row">
				<div @click.stop>
					<e-btn
						text="복제"
						:icon="require('@/assets/img/button-copy.png')"
						@click="$emit('copy', row.item)"
					/>
				</div>
			</template>
			<!--삭제 버튼-->
			<template #cell(delete)="row">
				<div @click.stop>
					<e-btn
						text="삭제"
						:icon="require('@/assets/img/button-delete.png')"
						:disabled="row.field.disabled || row.item.deleteDisabled"
						@click="$emit('delete', row.item)"
					/>
				</div>
			</template>
			<!--진행률-->
			<template #cell(progress)="row">
				<small-doughnut :ratio="Number(row.value)" />
			</template>
		</b-table>
		<div class="table-pagination" v-if="isPagination">
			<b-pagination
				v-model="current"
				:per-page="perPage"
				:total-rows="count"
				hide-ellipsis
				@change="onPage"
			>
				<template #first-text>
					<img
						class="rotate-180"
						src="@/assets/img/arrow-next-all.png"
					/>
				</template>
				<template #prev-text>
					<img
						class="rotate-180"
						src="@/assets/img/arrow-next.png"
					/>
				</template>
				<template #next-text>
					<img src="@/assets/img/arrow-next.png" />
				</template>
				<template #last-text>
					<img src="@/assets/img/arrow-next-all.png" />
				</template>
			</b-pagination>
			<b-form-select
				v-model="perPage"
				:options="pageOptions"
			></b-form-select>
		</div>
	</div>
</template>

<script>
import { VUE2_DATEPICKER_LANG } from '@/utils/datepickerLocale'
import Badges from '../button/badges.vue'
import smallDoughnut from '../chart/smallDoughnut.vue'
import status from '@/utils/status'

export default {
	props: {
		title: {
			type: String,
			default: ''
		},
		headers: {
			type: Array,
			default: () => []
		},
		list: {
			type: Array,
			default: () => []
		},
		isLoading: {
			type: Boolean,
			default: false
		},
		isRange: {
			type: Boolean,
			default: false
		},
		isPagination: {
			type: Boolean,
			default: false
		},
		count: {
			type: Number,
			default: 1
		},
		page: {
			type: String,
			default: ''
		},
		noContent: {
			type: String,
			default: `<p class="no-content">등록된 항목이 없습니다.</p>`
		},
		isSelected: {
			type: Boolean,
			default: false
		},
		selected: {
			type: Array,
			default: () => []
		},
		params: {
			type: Object,
			default: () => {}
		}
	},
	components: {
		smallDoughnut,
		Badges
	},
	created() {
		if (this.params?.page) {
			this.current = this.params.page;
		}
		if (this.params?.count) {
			this.perPage = this.params.count;
		}
		if (this.isRange) {
			this.range[0] = this.params?.filter?.startDatetime
			this.range[1] = this.params?.filter?.endDatetime
		}
	},
	computed: {
		total() {
			return this.count / this.perPage;
		},
		selectedClass() {
			if (this.selected.length === 0) {
				return 'off';
			}
			return this.selected.length < this.list.length ? 'indeterminate' : 'on';
		},
		isDetailOrDashboard() {
			return this.page === 'detail' || this.page === 'dashboard';
		},
		pageOptions() {
			if (this.isDetailOrDashboard) {
				return [
					{ text: '5개', value: 5 },
					{ text: '10개', value: 10 },
					{ text: '20개', value: 20 },
					{ text: '50개', value: 50 }
				];
			}
			return [
				{ text: '30개', value: 30 },
				{ text: '50개', value: 50 },
				{ text: '70개', value: 70 },
				{ text: '100개', value: 100 }
			];
		},
		fileUrl() {
			return process.env.VUE_APP_FILE_URL
		},
	},
	watch: {
		allChecked(val) {
			if (val) {
				this.$refs.table.selectAllRows()
			} else {
				this.$refs.table.clearSelected()
			}
		},
		// 체크 기능이 꺼지면(isSelected=false) b-table 은 내부 선택을 비우지만,
		// 그 시점엔 이미 비선택 모드라 row-selected 를 밖으로 내보내지 않는다.
		// 그래서 부모의 선택 목록과 머리글 체크 표시만 켜진 채로 남아
		// 화면(체크됨)과 실제 선택(0건)이 어긋난다.
		//
		// 2026-08-18 신고: "정산완료 탭에서 작업하다 정산예정으로 돌아와 검색하면
		// 체크가 안 된다" — 머리글 체크박스가 켜진 상태로 남아 다음 클릭이
		// '전체선택'이 아니라 '전체해제'로 동작한 것이었다.
		// 더 위험한 파생 증상으로, 부모의 선택 목록이 남아 화면에 체크가 없는데도
		// 직전 선택분으로 정산 등록이 가능했다.
		isSelected(val) {
			if (val) return
			this.allChecked = false
			if (this.$refs.table) this.$refs.table.clearSelected()
			if (this.selected.length) this.$emit('selected', [])
		},
		// 부모에서 selected가 빈 배열로 초기화되면 테이블 선택 상태도 초기화
		selected: {
			handler(val) {
				if (val && val.length === 0 && this.$refs.table) {
					this.$refs.table.clearSelected()
					this.allChecked = false
				}
			},
			deep: true
		},
		params: {
			deep: true,
			handler(newVal) {
				if (newVal?.page != null) this.current = newVal.page
				if (newVal?.count != null) this.perPage = newVal.count
			}
		},
		perPage(val) {
			this.$emit('onPage', 1, val)
		},
		page: {
			immediate: true,
			handler(val) {
				if (val === 'detail') {
					this.perPage = this.params?.count ?? 5
				} else if (val === 'dashboard') {
					this.perPage = this.params?.count ?? 10
				} else {
					this.perPage = this.params?.count ?? 30
				}
			}
		}
	},
	data() {
		return {
			current: 1,
			perPage: 30,
			range: [new Date(), new Date()],
			vue2DatepickerLang: VUE2_DATEPICKER_LANG,
			allChecked: false,
			status: status
		}
	},
	methods: {
		onRow(item, index, e) {
			this.$emit('onRow', item, index, e);
		},
		onPage(current) {
			this.$refs.table.$el.scrollTop = 0;
			this.$emit('onPage', current, this.perPage);
		},
		trClass(item) {
			if (item?.isNew) {
				return 'is-new';
			}
		},
		onRowSelected(items) {
			this.$emit('selected', items);
		},
		toHTML(value) {
			return value.replace(/\r?\n/g, '<br>');
		},
		getBadgeStyle(color) {
			return `background-color: var(--ehti-${color})`;
		}
	}
}
</script>

<style lang="scss" scoped>
.sortable-header {
	position: relative;
	display: block;
	width: 100%;
	cursor: pointer;

	.sort-img {
		position: absolute;
		top: 50%;
		right: 8px;
		width: 14px;
		height: 14px;
		transform: translateY(-50%);
	}
}

.cell-with-sub {
	p {
		margin: 0;
	}
	// 소속 그룹·구분 — 항목명보다 작고 흐리게
	.cell-sub {
		display: block;
		margin-top: 2px;
		font-size: 0.75rem;
		line-height: 1.2;
		color: #8c8c8c;
	}
}

.sr-only {
	visibility: hidden;
	font-size: 0;
	width: 0;
}

.rotate-180 {
	transform: rotate(180deg);
}

.download-text {
	letter-spacing: 0;
}

.attachment-link {
	display: flex;
	width: 140px;
	margin: 5px 0;
}
:deep(.table) {
	&:not(caption) {
		.is-new {
			td {
				background-color: var(--ehti-secondarygreen);
			}
		}
	}
}
</style>
